import { Checkbox } from "@/components/ui/checkbox"
import { useColorModeValue } from "@/components/ui/color-mode"
import {
  type ApiTokenScope,
  PermissionFlag,
  type ResourceType,
} from "@/lib/apiTokens"
import { Box, HStack, Table, Text } from "@chakra-ui/react"
import { useTranslation } from "react-i18next"

// Resource types the user can grant scopes for. Mirrors the backend
// `ResourceType` enum in the order most relevant for end users.
const PICKER_RESOURCE_TYPES: ResourceType[] = [
  "tag",
  "tag_reading",
  "tag_event",
  "tag_template",
  "reader",
  "organisation",
  "user",
  "item",
]

const PICKER_PERMISSIONS = [
  { flag: PermissionFlag.READ, key: "read" },
  { flag: PermissionFlag.CREATE, key: "create" },
  { flag: PermissionFlag.UPDATE, key: "update" },
  { flag: PermissionFlag.DELETE, key: "delete" },
] as const

interface PermissionScopePickerProps {
  /** Current scopes — array of `{resource_type, permission (bitmask)}`. */
  value: ApiTokenScope[]
  onChange: (scopes: ApiTokenScope[]) => void
}

/**
 * Bitwise grid editor: rows are resource types, columns are permission flags
 * (READ/CREATE/UPDATE/DELETE). The picker stores scopes as a list of
 * ``{resource_type, permission}`` entries with `permission` as the OR-ed
 * bitmask of the checked flags. Resource types with no flag checked are
 * omitted from the result.
 */
export function PermissionScopePicker({
  value,
  onChange,
}: PermissionScopePickerProps) {
  const { t } = useTranslation("apiTokens")
  const headerColor = useColorModeValue("gray.700", "gray.300")
  const rowHover = useColorModeValue("gray.50", "whiteAlpha.50")

  // Lookup table: resource_type → bitmask. Easier to read/write than scanning
  // the array each time.
  const masks: Record<string, number> = {}
  for (const scope of value) {
    masks[scope.resource_type] = scope.permission
  }

  const setMask = (resource_type: ResourceType, mask: number) => {
    const next: ApiTokenScope[] = []
    for (const rt of PICKER_RESOURCE_TYPES) {
      const m = rt === resource_type ? mask : (masks[rt] ?? 0)
      if (m !== 0) next.push({ resource_type: rt, permission: m })
    }
    onChange(next)
  }

  const toggle = (
    resource_type: ResourceType,
    flag: number,
    checked: boolean,
  ) => {
    const current = masks[resource_type] ?? 0
    const next = checked ? current | flag : current & ~flag
    setMask(resource_type, next)
  }

  return (
    <Box>
      <Text fontSize="sm" color={headerColor} mb={3}>
        {t("scopes.help")}
      </Text>
      <Table.Root size="sm" variant="outline">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>{t("scopes.resource")}</Table.ColumnHeader>
            {PICKER_PERMISSIONS.map((p) => (
              <Table.ColumnHeader key={p.key} textAlign="center">
                {t(`scopes.flag.${p.key}`)}
              </Table.ColumnHeader>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {PICKER_RESOURCE_TYPES.map((rt) => {
            const current = masks[rt] ?? 0
            return (
              <Table.Row key={rt} _hover={{ bg: rowHover }}>
                <Table.Cell>
                  <Text fontFamily="mono" fontSize="sm">
                    {t(`scopes.resourceType.${rt}`, { defaultValue: rt })}
                  </Text>
                </Table.Cell>
                {PICKER_PERMISSIONS.map((p) => {
                  const checked = (current & p.flag) === p.flag
                  return (
                    <Table.Cell
                      key={p.key}
                      textAlign="center"
                      verticalAlign="middle"
                    >
                      <HStack justify="center">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(d) =>
                            toggle(rt, p.flag, !!d.checked)
                          }
                          aria-label={`${rt} ${p.key}`}
                        />
                      </HStack>
                    </Table.Cell>
                  )
                })}
              </Table.Row>
            )
          })}
        </Table.Body>
      </Table.Root>
    </Box>
  )
}
